<template>
  <Loader :is-loading="isLoading">
    <form id="" @submit.prevent="updateProjectType">
      <Card :title="$t('projectTypes.edition')" class="card p-5">
        <template #cardBody>
          <ProjectTypeForm :type="type"/>
        </template>
        <template #cardFooter>
          <div class="d-flex justify-content-end ">
            <Button type="cancel" btn-class="btn-sm me-2" btn-text="button.cancel"  @click.prevent="router.back" size="7px 30px"></Button>
            <Button type="submit" btn-class="btn-sm me-2" btn-text="button.save" size="7px 30px" :isDisabled="submitting" :isLoading="submitting"></Button>
          </div>
        </template>
      </Card>
    </form>
  </Loader>
</template>

<script setup>

import {computed, onMounted, ref, watch} from "vue";
import i18n from '@/plugins/i18n';
import {useStore} from "vuex";
import {useHasAbility} from "@/composables/hasAbility";
import { useLocales } from "@/composables/locales";
import {useSwal} from "@/composables/useSwal";
import ProjectTypeForm from "@/Modules/Projects/components/ProjectType/ProjectTypeForm.vue";
import {useRoute, useRouter} from "vue-router";
import Card from "@/components/Card.vue";
import Button from "@/components/Button.vue";
import Loader from "@/components/Loader.vue";

const route = useRoute()
const router = useRouter()
const store = useStore()
const {locale, datatableLocaleUrl} = useLocales()
const { can } = useHasAbility()
const {showSwal} = useSwal()

const isLoading = ref(false)
const submitting = ref(false)

const type = ref({
  id: null,
  name: "",
  description: "",
  configs: [
    {
      milestone_category_id: '',
      milestone_category_name: '',
      delay_value: '',
      delay_unit: '',
      milestone_categories_progress: '',
      status: [],
      tasks: [],
      position: 0
    }
  ],
  tables: []
})

const currentType = computed(() => store.getters['projectCategories/getCurrentProjectCategory'])

watch(() => currentType.value, (newValue) => {
  if(newValue.id && route.params.id){
    type.value.id = newValue.id
    type.value.name= newValue.name
    type.value.prefix= newValue.prefix
    type.value.color= newValue.color
    type.value.project_category_group_id= newValue.project_category_group_id
    type.value.description = newValue.description
    type.value.configs = newValue.project_category_milestone_categories?.map((step, index) => ({
      position: index +1 ,
      milestone_category_id: step.milestone_category_id,
      milestone_category_name: step?.milestone_category?.title,
      milestone_categories_progress: parseInt(step.milestone_categories_progress),
      delay_value: step.delay_value || '',
      delay_unit: step.delay_unit.value,
      status: step.category_milestone_statuses?.map(task => task.task_status_id),  
      tasks: step.tasks?.map(task => {
        return {
          id: task.id,
          title : task?.title,
          task_priority_id: task?.taskPriority?.id,
          delay_value: task.delay_value || '',
          delay_unit: task.delay_unit.value,
          delay_value_alert: task.delay_value_alert || '',
          delay_unit_alert: task.delay_unit_alert.value,
          delay_value_critical: task.delay_value_critical || '',
          delay_unit_critical: task.delay_unit_critical.value,
          need_review: task.need_review,
          checklists: task.checklists?.map(el => ({
            id: el?.id,
            title: el?.title,
            delay_value_alert: el.delay_value_alert || '',
            delay_unit_alert: el.delay_unit_alert,
            delay_value_critical: el.delay_value_critical || '',
            delay_unit_critical: el.delay_unit_critical,
          }))
        }
      }),
    }))
    type.value.tables = newValue.project_category_tables?.tables || []
  }else {
    type.value = null
    type.value = {
      id: "",
      name: "",
      description: "",
      project_category_group_id: "",
      configs: [],
      tables: []
    }
  }
}, {immediate: true})

const updateProjectType = () => {
  submitting.value = true
  store.dispatch('projectCategories/updateProjectCategory', type.value)
      .then((response) => {
        window.toastr['success'](i18n.global.t('notification.project_type_updated'))
        router.push({ name: 'settings-projects-categories' })
      })
      .catch(err => {
      }).finally(() => submitting.value = false)
}

const loadCurrentProjectType = async () => {
  if(route.params.id){
    await store.dispatch('projectCategories/loadCurrentProjectCategories', route.params.id)
  }
}

function loadData() {
  isLoading.value = true
  Promise.all([
    store.dispatch('taskStatus/loadTaskStatus'),
    store.dispatch('milestoneCategories/loadTaskMilestones'),
    store.dispatch('taskPriorities/loadTaskPriorities'),
    store.dispatch("projectCategories/loadCategoryGroups"), 

    loadCurrentProjectType()
  ]).finally(() => {
    isLoading.value = false
  })
}


onMounted(() => {
  loadData()
})
</script>
